import React from 'react';
import { LayoutChangeEvent, StyleSheet } from 'react-native';

import { LineSeparator } from '@/components/LineSeparator';
import { SocialCurrencyActiveIcon } from '@/components/SocialCurrencyActiveIcon';
import { Text } from '@/components/TextComponent';
import { Box, Row } from '@/components/lib/components';
import { ds } from '@/components/lib/design.system';
import { BetSlipMarketName } from '@/feature/betslip-sbk/components/BetSlipMarketName';
import { OptionName } from '@/feature/betslip-sbk/components/OptionName';
import { useSbkBetSlipStore } from '@/feature/betslip-sbk/hooks/use-sbk-betslip-store';
import { betPayoutSelector } from '@/feature/betslip-sbk/utils/betslip-selectors';
import { formatEventTeams } from '@/feature/betslip-sbk/utils/formatter';
import { useFormatCurrency } from '@/hooks/use-format-currency';
import { useHasSocialWallet } from '@/hooks/use-has-social-wallet';
import { useIsCashCurrency } from '@/hooks/use-social-currency';
import { isOptionSuspended, isProducerDown } from '@/utils/option-status';
import { useShallow } from 'zustand/react/shallow';

type EntrySummaryItemSingleProps = {
    betId: string;
    columnWidths: { entry: number; payout: number };
    createLayoutHandler: (column: 'entry' | 'payout') => (event: LayoutChangeEvent) => void;
};

const styles = StyleSheet.create({
    marketLineSeparator: {
        height: 16,
    },
});

const SUSPENDED_COLOR = 'neutral400' as const;

export const EntrySummaryItemSingle = ({ betId, columnWidths, createLayoutHandler }: EntrySummaryItemSingleProps) => {
    const { formatCurrency } = useFormatCurrency();
    const hasSocialWallet = useHasSocialWallet();

    const { stake, optionId, eventId, marketId, isSuspended, eventTeams } = useSbkBetSlipStore(
        useShallow(state => {
            const bet = state.bets[betId];
            const selection = state.selections[betId];
            const option = state.options[selection?.optionId];
            const market = state.markets[selection?.marketId];
            const event = state.events[selection?.eventId];

            const suspended = option
                ? isOptionSuspended(
                      option.status,
                      isProducerDown(state.producerStatuses, market?.supplier),
                      market?.published
                  )
                : false;

            return {
                stake: bet?.stake ?? 0,
                optionId: selection?.optionId,
                eventId: selection?.eventId,
                marketId: selection?.marketId,
                isSuspended: suspended,
                eventTeams: event ? formatEventTeams(event) : '',
            };
        })
    );

    const isCashCurrency = useIsCashCurrency();
    const payout = useSbkBetSlipStore(betPayoutSelector(betId, isCashCurrency));

    if (!optionId || !eventId || !marketId) {
        return null;
    }

    return (
        <Box height={80}>
            <Row py="s16" alignItems="center" justifyContent="space-between">
                <Box flex={1} pr="s16" gap="s4">
                    <Text variant="bodySmall" color={isSuspended ? SUSPENDED_COLOR : 'neutral300'} numberOfLines={1}>
                        {eventTeams}
                    </Text>
                    <Row flexShrink={1} gap="s8" alignItems="center">
                        <OptionName
                            optionId={optionId}
                            variant="titleMedium"
                            color={isSuspended ? SUSPENDED_COLOR : ds.colorToken.text.primary}
                            numberOfLines={1}
                        />
                        <LineSeparator vertical style={styles.marketLineSeparator} />
                        <Box flexShrink={1}>
                            <BetSlipMarketName
                                eventId={eventId}
                                marketId={marketId}
                                variant="bodyMedium"
                                color={isSuspended ? SUSPENDED_COLOR : 'neutral300'}
                                numberOfLines={1}
                            />
                        </Box>
                    </Row>
                </Box>
                <Box minWidth={columnWidths.entry} pr="s16">
                    <Row alignItems="center" justifyContent="flex-end" gap="s4" onLayout={createLayoutHandler('entry')}>
                        {hasSocialWallet ? (
                            <Box opacity={isSuspended ? 0.3 : 1}>
                                <SocialCurrencyActiveIcon />
                            </Box>
                        ) : null}
                        <Text
                            variant="bodyMedium"
                            color={isSuspended ? SUSPENDED_COLOR : ds.colorToken.text.primary}
                            textDecorationLine={isSuspended ? 'line-through' : 'none'}
                        >
                            {formatCurrency(stake)}
                        </Text>
                    </Row>
                </Box>
                <Box minWidth={columnWidths.payout}>
                    <Row
                        alignItems="center"
                        justifyContent="flex-end"
                        gap="s4"
                        onLayout={createLayoutHandler('payout')}
                    >
                        {hasSocialWallet ? (
                            <Box opacity={isSuspended ? 0.3 : 1}>
                                <SocialCurrencyActiveIcon />
                            </Box>
                        ) : null}
                        <Text
                            variant="bodyMedium"
                            color={isSuspended ? SUSPENDED_COLOR : ds.colorToken.text.primary}
                            textDecorationLine={isSuspended ? 'line-through' : 'none'}
                        >
                            {formatCurrency(payout)}
                        </Text>
                    </Row>
                </Box>
            </Row>
            <LineSeparator />
        </Box>
    );
};
